import { AfterViewInit, ChangeDetectorRef, Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { ColumnBase, GridComponent } from '@progress/kendo-angular-grid';
import _ from 'lodash';
import { merge, Observable, Subject } from 'rxjs';
import { auditTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BrickInstancesSelectors } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.selectors';
import { ColumnSetting, GridSettings } from '../../samwebDescription/samweb/dashboard/grid-column-settings';
import {
   BrickGridColumnChanged,
   BrickGridColumnOrderChanged,
} from '../../store/dashboard/brick-wall/brick-behaviour-state/brick-grids-state/brick-grids.actions';
import { BrickGridsState } from '../../store/dashboard/brick-wall/brick-behaviour-state/brick-grids-state/brick-grids.state';
import { BrickContainerComponent } from '../dashboard/brick-container/brick-container.component';

@Directive({
   selector: 'kendo-grid:[samwebPersistKendoGrid]',
})
export class PersistKendoGridDirective implements OnInit, AfterViewInit, OnDestroy {
   constructor(private kendoGrid: GridComponent, private brick: BrickContainerComponent, private store: Store, private changeDetectorRef: ChangeDetectorRef) {}

   @Input()
   public gridId = 'default';

   private destroy$ = new Subject();

   public get brickInstanceId() {
      return this.brick.brickInstanceId;
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngAfterViewInit(): void {
      let updateGridData$: Observable<(brickInstanceId: string, gridId: string) => GridSettings>;
      if (this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(this.brickInstanceId)?.brickInstance?.brickDefinitionId === 'directoryBrick') {
         updateGridData$ = this.store.select(BrickGridsState.getBrickColumnConfigurationForDirectoryBrick);
      } else {
         updateGridData$ = this.store.select(BrickGridsState.getBrickColumnConfiguration);
      }
      updateGridData$
         .pipe(
            map((getSetting) => getSetting(this.brickInstanceId, this.gridId)),
            filter((configuration) => !_.isNil(configuration) && !_.isUndefined(configuration)),
            tap((configuration) =>
               _.values(configuration.columnsConfig).forEach((columnConfig: ColumnSetting) => {
                  this.kendoGrid.columns
                     .filter((column) => _.has(column, 'field') && _.get(column, 'field') === columnConfig.field)
                     .forEach((column) => {
                        column.hidden = columnConfig.isHidden;
                        column.orderIndex = columnConfig.orderIndex;
                        // we need to set the width for the update subscription later, not sure why
                        column.width = column.width;
                        if (!_.isNil(columnConfig.width)) {
                           column.width = columnConfig.width;
                        }
                     });
               })
            ),
            tap((configuration) => {
               // order all columns, also columns they are not changed, to get a correct ordering!
               const allColumns: ColumnBase[] = this.kendoGrid.columns.filter((columnBase) => !columnBase.hidden);
               const columnEntriesWithoutSpecificOrder: ColumnBase[] = _.chain(allColumns)
                  .filter(
                     (column) =>
                        !_.has(column, 'field') ||
                        _.isNil(_.get(configuration.columnsConfig, _.get(column, 'field'))) ||
                        _.isNil(_.get(configuration.columnsConfig, _.get(column, 'field')).orderIndex)
                  )
                  .value();
               const assignedNumbers: number[] = _.chain(allColumns)
                  .without(...columnEntriesWithoutSpecificOrder)
                  .map((columnBase) => columnBase.orderIndex)
                  .value();
               const availableNumbers: number[] = _.chain(_.range(0, allColumns.length))
                  .without(...assignedNumbers)
                  .value();
               this.kendoGrid.columns
                  .filter((columnBase) => !columnBase.hidden)
                  .forEach((columnBase) => {
                     if (columnEntriesWithoutSpecificOrder.includes(columnBase)) {
                        columnBase.orderIndex = availableNumbers.shift();
                     }
                  });
            }),
            auditTime(10),
            tap((gridSettings) => {
               setTimeout(() => {
                  this.changeDetectorRef.markForCheck();
                  this.changeDetectorRef.detectChanges();
               }, 10);
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnInit(): void {
      this.kendoGrid.columnReorder
         .pipe(
            filter((reorder) => _.has(reorder.column, 'field')),
            map((reorder) => {
               const column = <ColumnSetting>{
                  field: _.get(reorder.column, 'field'),
                  width: reorder.column.width,
                  orderIndex: reorder.column.orderIndex,
                  isHidden: reorder.column.hidden,
               };
               return this.store.dispatch(new BrickGridColumnOrderChanged(this.brickInstanceId, this.gridId, column, reorder.newIndex, reorder.oldIndex));
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      merge(
         this.kendoGrid.columnLockedChange.pipe(map((lockedChange) => lockedChange.columns)),
         this.kendoGrid.columnResize.pipe(map((resizeArgs) => resizeArgs.map((resizreArg) => resizreArg.column))),
         this.kendoGrid.columnVisibilityChange.pipe(
            map((visibilityChange) => {
               visibilityChange.columns.forEach((columnBase) => (columnBase.orderIndex = undefined));
               return visibilityChange.columns;
            })
         )
      )
         .pipe(
            // save all column values instead of changed.
            map(() => this.kendoGrid.columns),
            map((columns) => columns.filter((columnBase) => _.has(columnBase, 'field'))),
            filter((columnBase) => columnBase.length > 0),
            map((columnBase) =>
               columnBase.map(
                  (column) =>
                     <ColumnSetting>{
                        field: _.get(column, 'field'),
                        width: column.width,
                        isHidden: column.hidden,
                     }
               )
            ),
            switchMap((columnSettings) => this.store.dispatch(new BrickGridColumnChanged(this.brickInstanceId, this.gridId, columnSettings))),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }
}
